import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { QuickReplies } from "@/components/chat/QuickReplies";
import { MessagesSquare, Bot, Plus, Trash2 } from "lucide-react";
import { SecretValue } from "@/components/SecretValue";

const inp = "w-full rounded-lg border border-[#1F293D] bg-[#0B0F17] px-3 py-2 text-sm text-slate-100 outline-none focus:border-sky-500";
const L = ({ label, children }) => <label className="block"><span className="mb-1 block text-xs text-slate-400">{label}</span>{children}</label>;

const FaqEditor = ({ faq, onChange }) => {
  const upd = (i, k, v) => onChange(faq.map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  return (
    <div data-testid="chat-faq-editor" className="space-y-2">
      <span className="block text-xs text-slate-400">Perguntas frequentes (aparecem no app)</span>
      {faq.map((f, i) => (
        <div key={i} className="flex gap-2 rounded-lg border border-[#1F293D] bg-[#0F141F] p-2">
          <div className="flex-1 space-y-1.5">
            <input data-testid={`chat-faq-q-${i}`} value={f.q} onChange={(e) => upd(i, "q", e.target.value)} placeholder="Pergunta" maxLength={200} className={inp} />
            <textarea data-testid={`chat-faq-a-${i}`} value={f.a} onChange={(e) => upd(i, "a", e.target.value)} placeholder="Resposta" rows={2} maxLength={1500} className={inp} />
          </div>
          <button data-testid={`chat-faq-del-${i}`} onClick={() => onChange(faq.filter((_, j) => j !== i))} className="self-start p-1 text-slate-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <button data-testid="chat-faq-add" onClick={() => onChange([...faq, { q: "", a: "" }])} className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-[#1F293D] px-3 py-1.5 text-xs text-slate-300 hover:text-white"><Plus className="h-3.5 w-3.5" /> Adicionar pergunta</button>
    </div>
  );
};

export const ChatSettings = () => {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/chat/config").then((r) => setC(r.data)); }, []);
  if (!c) return null;
  const save = async (extra = {}) => {
    try {
      const { data } = await api.put("/chat/config", {
        title: c.title, hours: c.hours, faq: c.faq, agent_name: c.agent_name, greeting: c.greeting, offline_text: c.offline_text, ai_kb: c.ai_kb, ...extra,
      });
      setC(data);
      toast.success("Chat do site salvo");
    } catch (e) { toast.error(apiError(e)); }
  };
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });
  return (
    <div data-testid="chat-settings" className="mb-5 rounded-xl border border-[#1F293D] bg-[#111827] p-5">
      <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-slate-200"><MessagesSquare className="h-5 w-5 text-sky-400" /> Chat do site</h3>
      <div className="space-y-3">
        <div className="rounded-lg border border-[#1F293D] bg-[#0F141F] px-3 py-2 text-xs text-slate-400">
          O chat só funciona pela ponte do servidor do Futmob, então o navegador do cliente nunca vê o endereço do CRM.
          <div className="mt-1">Chave da ponte (CRM_BRIDGE_KEY no Futmob): <SecretValue testid="chat-bridge-key" masked="••••••••" field="chat_bridge_key" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <L label="Nome da empresa no chat"><input data-testid="chat-title" value={c.title} onChange={set("title")} maxLength={40} className={inp} /></L>
          <L label="Nome da atendente (persona)"><input data-testid="chat-agent-name" value={c.agent_name || ""} onChange={set("agent_name")} maxLength={30} className={inp} /></L>
          
        </div>
        <L label="Horário de atendimento (aparece no app)"><input data-testid="chat-hours" value={c.hours || ""} onChange={set("hours")} maxLength={200} className={inp} /></L>
        <FaqEditor faq={c.faq || []} onChange={(faq) => setC({ ...c, faq })} />
        <L label="Mensagem quando ninguém está disponível (e a IA está desligada)"><input data-testid="chat-offline-text" value={c.offline_text} onChange={set("offline_text")} maxLength={300} className={inp} /></L>

        <div className="rounded-lg border border-violet-500/25 bg-violet-500/5 p-3">
          <label data-testid="chat-ai-toggle-row" className="flex cursor-pointer items-center justify-between gap-3">
            <span className="text-sm text-slate-200"><Bot className="mr-1.5 inline h-4 w-4 text-violet-300" />Agente de IA (Claude Sonnet 4.5)
              <span className="block text-xs text-slate-500">Liga/desliga geral da Bia: ela inicia todo atendimento e chama um atendente quando foge das capacidades dela. Para quando um atendente responde ou o cliente pede um humano.</span>
            </span>
            <input data-testid="chat-ai-toggle" type="checkbox" className="h-5 w-5 accent-violet-500" checked={!!c.ai_enabled}
              onChange={(e) => save({ ai_enabled: e.target.checked })} />
          </label>
          <L label="Base de conhecimento da IA (planos, preços, como instalar, regras...)">
            <textarea data-testid="chat-ai-kb" rows={8} value={c.ai_kb} onChange={set("ai_kb")} maxLength={30000} className={`${inp} mt-2 font-mono text-xs`}
              placeholder="Ex.: Planos: mensal R$ 30, trimestral R$ 80... Como instalar no Smart TV: ..." />
          </L>
          <p className="mt-1 text-[11px] text-slate-500">A IA só informa vencimento, plano e link PIX depois que o cliente confirma o usuário do painel. Nunca envia senhas.</p>
        </div>
        <button data-testid="chat-settings-save" onClick={() => save()} className="rounded-lg bg-sky-500 px-4 py-2 text-sm font-bold text-slate-950">Salvar chat do site</button>
        <QuickReplies />
      </div>
    </div>
  );
};
